import React from 'react';

import { ImageWithFallback } from '../image-with-fallback';

/**
 * @typedef {object} DisplayInformation
 * @property {string} [logoUrl] - URL for the logo image
 * @property {string} [iconUrl] - URL for the icon image
 * @property {string} [alternateIconUrl] - URL for the alternate icon image
 */

/**
 * @typedef {object} LogoWithFallbackProps
 * @property {DisplayInformation} [displayInformation] - Object containing URLs for different logo types
 * @property {'icon'|'alternateIcon'|'logo'} type - The type of logo to display
 * @property {string} [iconFallback] - Fallback image for icon type
 * @property {string} [logoFallback] - Fallback image for logo type
 */

const emptyDisplayInformation = {};

/**
 * Logo component that selects appropriate logo type and provides fallback support
 * @param {LogoWithFallbackProps & React.ImgHTMLAttributes<HTMLImageElement>} props
 * @returns {React.JSX.Element}
 */
export const LogoWithFallback = ({ displayInformation = emptyDisplayInformation, type, iconFallback = '', logoFallback = '', ...rest }) => {
  const fallback = type === 'logo' ? logoFallback : iconFallback;
  if (!displayInformation) {
    return <ImageWithFallback src={fallback} srcOnError={fallback} {...rest} />;
  }

  let url;
  switch (type) {
    case 'icon':
      url = displayInformation.iconUrl;
      break;
    case 'alternateIcon':
      url = displayInformation.alternateIconUrl;
      break;
    case 'logo':
      url = displayInformation.logoUrl;
      break;
    default:
      url = '';
  }

  return <ImageWithFallback src={url} srcOnError={fallback} {...rest} />;
};
